import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/modules/auth'
import { TripChatApi } from '@/shared/api/tripChat'
import type { TripChatReadState, TripCrewMessage } from '@/shared/api/types'
import { useRealtimeTripChat } from './useRealtimeTripChat'

interface UseTripChatReturn {
  messages: TripCrewMessage[]
  isLoading: boolean
  error: string | null
  typingUserIds: string[]
  readStates: TripChatReadState[]
  sendMessage: (body: string, parentId?: string) => Promise<TripCrewMessage>
  updateMessage: (messageId: string, body: string) => Promise<void>
  deleteMessage: (messageId: string) => Promise<void>
  broadcastTyping: (isTyping: boolean) => void
}

export function useTripChat(tripId: string | undefined): UseTripChatReturn {
  const { getAccessToken, user } = useAuth()
  const api = useMemo(() => new TripChatApi(getAccessToken), [getAccessToken])
  const [messages, setMessages] = useState<TripCrewMessage[]>([])
  const [readStates, setReadStates] = useState<TripChatReadState[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [typingUserIds, setTypingUserIds] = useState<string[]>([])
  const typingTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const markReadDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadMessages = useCallback(async () => {
    if (!tripId) return
    setIsLoading(true)
    setError(null)
    try {
      const [msgs, states] = await Promise.all([
        api.listMessages(tripId, { limit: 50 }),
        api.listReadStates(tripId),
      ])
      setMessages(msgs)
      setReadStates(states)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chat')
    } finally {
      setIsLoading(false)
    }
  }, [api, tripId])

  // Load on mount + mark read (debounced to survive React strict-mode double-invoke)
  useEffect(() => {
    loadMessages()

    if (!tripId) return
    if (markReadDebounceRef.current) clearTimeout(markReadDebounceRef.current)
    markReadDebounceRef.current = setTimeout(async () => {
      try {
        const state = await api.markRead(tripId)
        setReadStates((prev) => {
          const filtered = prev.filter(
            (s) => !(s.trip_id === state.trip_id && s.user_id === state.user_id)
          )
          return [...filtered, state]
        })
      } catch {
        // non-critical
      }
    }, 400)

    return () => {
      if (markReadDebounceRef.current) clearTimeout(markReadDebounceRef.current)
    }
  }, [loadMessages, api, tripId])

  // ── Broadcast event handlers ──────────────────────────────────────────────
  // The backend sends a fully-formed message payload so we can update state
  // directly — no refetch needed.

  const handleNewMessage = useCallback((message: TripCrewMessage) => {
    setMessages((prev) => {
      // Ignore if we already have it (our own optimistic insert)
      if (prev.some((m) => m.id === message.id)) return prev
      return [...prev, message]
    })
  }, [])

  const handleUpdatedMessage = useCallback((message: TripCrewMessage) => {
    setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, ...message } : m)))
  }, [])

  const handleDeletedMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== messageId))
  }, [])

  const handleTypingChange = useCallback(
    (userId: string, isTyping: boolean) => {
      if (userId === user?.id) return
      setTypingUserIds((prev) => {
        if (isTyping) {
          if (prev.includes(userId)) return prev
          return [...prev, userId]
        }
        return prev.filter((id) => id !== userId)
      })
      if (isTyping) {
        if (typingTimersRef.current[userId]) clearTimeout(typingTimersRef.current[userId])
        typingTimersRef.current[userId] = setTimeout(() => {
          setTypingUserIds((prev) => prev.filter((id) => id !== userId))
        }, 4000)
      } else {
        if (typingTimersRef.current[userId]) {
          clearTimeout(typingTimersRef.current[userId])
          delete typingTimersRef.current[userId]
        }
      }
    },
    [user?.id]
  )

  const { broadcastTyping: broadcastTypingRaw } = useRealtimeTripChat({
    tripId: tripId ?? '',
    enabled: Boolean(tripId),
    onNewMessage: handleNewMessage,
    onUpdatedMessage: handleUpdatedMessage,
    onDeletedMessage: handleDeletedMessage,
    onTypingChange: handleTypingChange,
  })

  const typingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const broadcastTyping = useCallback(
    (isTyping: boolean) => {
      if (!tripId) return
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current)
      broadcastTypingRaw(isTyping)
      if (isTyping) {
        typingDebounceRef.current = setTimeout(() => {
          broadcastTypingRaw(false)
        }, 3000)
      }
    },
    [tripId, broadcastTypingRaw]
  )

  const sendMessage = useCallback(
    async (body: string, parentId?: string): Promise<TripCrewMessage> => {
      if (!tripId) throw new Error('Trip ID required')
      const message = await api.sendMessage(tripId, { body, parentId })
      // Optimistic insert so the sender sees their message before the broadcast arrives
      setMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev
        return [...prev, message]
      })
      return message
    },
    [api, tripId]
  )

  const updateMessage = useCallback(
    async (messageId: string, body: string): Promise<void> => {
      if (!tripId) throw new Error('Trip ID required')
      const updated = await api.updateMessage(tripId, messageId, { body })
      // Optimistic update; broadcast will arrive shortly and reconcile
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, ...updated } : m)))
    },
    [api, tripId]
  )

  const deleteMessage = useCallback(
    async (messageId: string): Promise<void> => {
      if (!tripId) throw new Error('Trip ID required')
      // Optimistic removal; broadcast will confirm for other members
      setMessages((prev) => prev.filter((m) => m.id !== messageId))
      await api.deleteMessage(tripId, messageId)
    },
    [api, tripId]
  )

  return {
    messages,
    isLoading,
    error,
    typingUserIds,
    readStates,
    sendMessage,
    updateMessage,
    deleteMessage,
    broadcastTyping,
  }
}
